<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts'
import {
  HelpCircle,
  Keyboard,
  Lightbulb,
  BookOpen,
  ExternalLink
} from 'lucide-vue-next'

const authStore = useAuthStore()
const { allShortcuts } = useKeyboardShortcuts()

const show = ref(false)
const activeTab = ref<'shortcuts' | 'tips'>('shortcuts')

const isMac = navigator.platform.toUpperCase().includes('MAC')
const modifierSymbol = isMac ? '\u2318' : 'Ctrl'

const isStaff = computed(() => authStore.isStaff)

interface GroupedShortcuts {
  category: string
  shortcuts: { id: string; description: string }[]
}

const groupedShortcuts = computed((): GroupedShortcuts[] => {
  const map = new Map<string, { id: string; description: string }[]>()
  for (const s of allShortcuts.value) {
    const cat = s.category || 'General'
    if (!map.has(cat)) map.set(cat, [])
    map.get(cat)!.push({ id: s.id, description: s.description })
  }
  const result: GroupedShortcuts[] = []
  for (const [category, shortcuts] of map) {
    result.push({ category, shortcuts })
  }
  return result
})

interface Tip {
  text: string
  staffOnly?: boolean
  clientOnly?: boolean
}

const tips: Tip[] = [
  { text: `Press ${isMac ? '\u2318' : 'Ctrl'}+K anywhere to open the Command Palette — search, navigate, or run actions without touching the mouse.` },
  { text: 'Press T on the ticket list to enter Triage Mode — use J/K to navigate, S/A/P to set status, assign, or cycle priority.', staffOnly: true },
  { text: 'The auto-timer starts when you type a comment on a ticket. After submitting, it offers to log the time for you.', staffOnly: true },
  { text: 'Click the bell icon in the top bar to open the Notification Center — snooze, dismiss, or navigate to any event.', staffOnly: true },
  { text: 'When you resolve a ticket, you can send a one-click CSAT survey to the requester from the ticket sidebar.', staffOnly: true },
  { text: 'Client Health badges appear next to client names — hover to see the per-dimension breakdown (tickets, SLA, CSAT, devices).', staffOnly: true },
  { text: 'When multiple people view the same ticket, you\'ll see their avatars at the top and a typing indicator when someone is composing.', staffOnly: true },
  { text: 'Press Ctrl+Enter to submit comments and notes in tickets and projects.' },
  { text: 'Use the Quick Todos widget in the top bar to add tasks without leaving your page.', staffOnly: true },
  { text: 'Hover over the Calendar icon to see your team\'s weekly schedule at a glance.', staffOnly: true },
  { text: 'Escape closes any open modal, dialog, or dropdown.' },
  { text: 'Use the Password Manager burn links to share secrets that self-destruct after viewing.' },
  { text: 'The Knowledge Base has articles that can help with common questions.' , clientOnly: true },
  { text: 'Dark mode can be toggled with the sun/moon icon in the top right.' },
  { text: 'Reports can be scheduled for automatic recurring delivery via email.', staffOnly: true },
]

const filteredTips = computed(() => tips.filter(tip => {
  if (tip.staffOnly && !isStaff.value) return false
  if (tip.clientOnly && isStaff.value) return false
  return true
}))

function formatKey(id: string): string[][] {
  if (id.includes(' ')) {
    return id.split(' ').map(part => formatSingleCombo(part))
  }
  return [formatSingleCombo(id)]
}

function formatSingleCombo(id: string): string[] {
  const parts = id.split('+')
  return parts.map(part => {
    if (part === 'ctrl') return modifierSymbol
    if (part === 'shift') return 'Shift'
    if (part === 'alt') return isMac ? '\u2325' : 'Alt'
    if (part === '?') return '?'
    return part.toUpperCase()
  })
}

let hideTimeout: ReturnType<typeof setTimeout> | null = null

function handleMouseEnter() {
  if (hideTimeout) {
    clearTimeout(hideTimeout)
    hideTimeout = null
  }
  show.value = true
}

function handleMouseLeave() {
  hideTimeout = setTimeout(() => {
    show.value = false
  }, 150)
}

function close() {
  show.value = false
}
</script>

<template>
  <div
    class="relative"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Trigger Button -->
    <button
      class="relative p-2 text-surface-400 hover:text-surface-100 hover:bg-surface-800 rounded-lg transition-colors"
      title="Help & Shortcuts"
    >
      <HelpCircle class="w-5 h-5" />
    </button>

    <!-- Dropdown Panel -->
    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="show"
        class="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-[380px] max-w-[380px] bg-surface-800 border border-surface-700 rounded-xl shadow-2xl z-50"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        <!-- Header -->
        <div class="flex items-center gap-2 p-3 border-b border-surface-700">
          <HelpCircle class="w-4 h-4 text-primary-400" />
          <span class="text-sm font-medium text-surface-100">Help & Shortcuts</span>
        </div>

        <!-- Tabs -->
        <div class="flex border-b border-surface-700">
          <button
            @click="activeTab = 'shortcuts'"
            class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors"
            :class="activeTab === 'shortcuts'
              ? 'text-primary-400 border-b-2 border-primary-400'
              : 'text-surface-400 hover:text-surface-200'"
          >
            <Keyboard class="w-3.5 h-3.5" />
            Shortcuts
          </button>
          <button
            @click="activeTab = 'tips'"
            class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors"
            :class="activeTab === 'tips'
              ? 'text-primary-400 border-b-2 border-primary-400'
              : 'text-surface-400 hover:text-surface-200'"
          >
            <Lightbulb class="w-3.5 h-3.5" />
            Tips
          </button>
        </div>

        <!-- Content -->
        <div class="max-h-[400px] overflow-y-auto">
          <!-- Shortcuts Tab -->
          <div v-if="activeTab === 'shortcuts'" class="p-3 space-y-4">
            <div v-for="group in groupedShortcuts" :key="group.category">
              <h4 class="text-[10px] font-semibold uppercase tracking-wider text-surface-500 mb-2">
                {{ group.category }}
              </h4>
              <div class="space-y-1.5">
                <div
                  v-for="shortcut in group.shortcuts"
                  :key="shortcut.id"
                  class="flex items-center justify-between py-1"
                >
                  <span class="text-xs text-surface-300">{{ shortcut.description }}</span>
                  <div class="flex items-center gap-1 flex-shrink-0 ml-3">
                    <template v-for="(combo, ci) in formatKey(shortcut.id)" :key="ci">
                      <span
                        v-if="ci > 0"
                        class="text-[10px] text-surface-500 mx-0.5"
                      >then</span>
                      <template v-for="(k, ki) in combo" :key="ki">
                        <span
                          v-if="ki > 0"
                          class="text-[10px] text-surface-500"
                        >+</span>
                        <kbd class="inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-[10px] font-medium text-surface-200 bg-surface-700 border border-surface-600 rounded shadow-sm">
                          {{ k }}
                        </kbd>
                      </template>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- Common shortcuts not in registry -->
            <div>
              <h4 class="text-[10px] font-semibold uppercase tracking-wider text-surface-500 mb-2">
                Everywhere
              </h4>
              <div class="space-y-1.5">
                <div class="flex items-center justify-between py-1">
                  <span class="text-xs text-surface-300">Close modal or dropdown</span>
                  <kbd class="inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-[10px] font-medium text-surface-200 bg-surface-700 border border-surface-600 rounded shadow-sm">ESC</kbd>
                </div>
                <div class="flex items-center justify-between py-1">
                  <span class="text-xs text-surface-300">Submit comment or note</span>
                  <div class="flex items-center gap-1">
                    <kbd class="inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-[10px] font-medium text-surface-200 bg-surface-700 border border-surface-600 rounded shadow-sm">{{ modifierSymbol }}</kbd>
                    <span class="text-[10px] text-surface-500">+</span>
                    <kbd class="inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-[10px] font-medium text-surface-200 bg-surface-700 border border-surface-600 rounded shadow-sm">ENTER</kbd>
                  </div>
                </div>
                <div class="flex items-center justify-between py-1">
                  <span class="text-xs text-surface-300">Save inline edit</span>
                  <kbd class="inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-[10px] font-medium text-surface-200 bg-surface-700 border border-surface-600 rounded shadow-sm">ENTER</kbd>
                </div>
              </div>
            </div>

            <div v-if="allShortcuts.length === 0 " class="text-center py-4 text-surface-500 text-xs">
              No shortcuts registered.
            </div>
          </div>

          <!-- Tips Tab -->
          <div v-if="activeTab === 'tips'" class="p-3">
            <div class="space-y-2">
              <div
                v-for="(tip, i) in filteredTips"
                :key="i"
                class="flex items-start gap-2.5 p-2.5 rounded-lg bg-surface-750 hover:bg-surface-700/50 transition-colors"
              >
                <Lightbulb class="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                <span class="text-xs text-surface-300 leading-relaxed">{{ tip.text }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-3 pb-3 pt-2 border-t border-surface-700">
          <router-link
            to="/help"
            @click="close"
            class="flex items-center justify-center gap-1.5 w-full text-xs text-primary-400 hover:text-primary-300 py-2 hover:bg-surface-700/50 rounded transition-colors"
          >
            <BookOpen class="w-3.5 h-3.5" />
            View Full Help Guide
            <ExternalLink class="w-3 h-3" />
          </router-link>
        </div>
      </div>
    </Transition>
  </div>
</template>
